import { type JSX, useCallback, useEffect, useState } from 'react';

import {
  feiertagAnlegen,
  feiertageEintragen,
  feiertagLoeschen,
  lesen,
  schliesstagAnlegen,
  schliesstagLoeschen,
  type BetroffenerTermin,
  type Feiertag,
  type Kalender as Kalenderdaten,
  type Schliesstag,
} from '../api/kalender';
import { ApiFehler } from '../api/fehler';
import { Meldung } from '../komponenten/Meldung';
import { Planung } from '../komponenten/Planung';
import { Rueckfrage } from '../komponenten/Rueckfrage';

/** Welcher Eintrag entfernt werden soll — die Rückfrage nennt ihn (CRM-104). */
type Nachfrage =
  | { readonly art: 'schliesstag'; readonly eintrag: Schliesstag }
  | { readonly art: 'feiertag'; readonly eintrag: Feiertag };

const LAENDER: Readonly<Record<string, string>> = {
  DE: 'bundesweit', BW: 'Baden-Württemberg', BY: 'Bayern', BE: 'Berlin', BB: 'Brandenburg',
  HB: 'Bremen', HH: 'Hamburg', HE: 'Hessen', MV: 'Mecklenburg-Vorpommern', NI: 'Niedersachsen',
  NW: 'Nordrhein-Westfalen', RP: 'Rheinland-Pfalz', SL: 'Saarland', SN: 'Sachsen',
  ST: 'Sachsen-Anhalt', SH: 'Schleswig-Holstein', TH: 'Thüringen',
};

const alsDatum = (iso: string): string => iso.split('-').reverse().join('.');

const tagform = new Intl.DateTimeFormat('de-DE', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  timeZone: 'UTC',
});
const monatform = new Intl.DateTimeFormat('de-DE', { month: 'long', year: 'numeric', timeZone: 'UTC' });

/** Der Montag vor dem Monatsersten — das Raster beginnt montags (DIN 1355-1). */
function rasterbeginn(jahr: number, monat: number): Date {
  const erster = new Date(Date.UTC(jahr, monat, 1));
  erster.setUTCDate(erster.getUTCDate() - ((erster.getUTCDay() + 6) % 7));
  return erster;
}

const alsTag = (d: Date): string => d.toISOString().slice(0, 10);

/**
 * Feiertage und Schließtage (FRS-08, FRS-09).
 *
 * Die gesetzlichen Feiertage je Bundesland verschieben die Zahlungsfrist
 * (FRS-05); an einem Schließtag entsteht am Standort kein Termin, und ein
 * schon stehender nimmt keine Buchung mehr an. Die Maske warnt, wenn der
 * Kalender weniger als zwölf Monate voraus reicht, und trägt die nächsten
 * Jahre mit einem Knopf nach.
 *
 * SEITENTYP PLANUNG (ÄA-41): Werkzeugleiste mit Jahr, Monat und Standort;
 * der Monat des Standorts in voller Breite, darunter die Tabellen des Jahres
 * zum Entfernen; rechts der gewählte Tag und die Formulare zum Eintragen.
 */
export function Kalender({ darfPflegen }: { readonly darfPflegen: boolean }): JSX.Element {
  const [jahr, setzeJahr] = useState(new Date().getFullYear());
  const [monat, setzeMonat] = useState(new Date().getMonth());
  /** Der im Raster gewählte Tag (`JJJJ-MM-TT`). */
  const [gewaehlt, setzeGewaehlt] = useState<string | null>(null);
  const [daten, setzeDaten] = useState<Kalenderdaten | null>(null);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [hinweis, setzeHinweis] = useState<string | null>(null);
  const [betroffen, setzeBetroffen] = useState<readonly BetroffenerTermin[]>([]);
  const [nachfrage, setzeNachfrage] = useState<Nachfrage | null>(null);

  const [sStandort, setzeSStandort] = useState('');
  const [sTag, setzeSTag] = useState('');
  const [sBezeichnung, setzeSBezeichnung] = useState('');
  const [fGeltung, setzeFGeltung] = useState('');
  const [fTag, setzeFTag] = useState('');
  const [fBezeichnung, setzeFBezeichnung] = useState('');

  const melde = useCallback((e: unknown) => {
    if (e instanceof ApiFehler) setzeFehler(e);
    else throw e;
  }, []);

  const holen = useCallback(async () => {
    try {
      const k = await lesen(jahr);
      setzeDaten(k);
      setzeFehler(null);
      if (sStandort === '' && k.standorte.length > 0) setzeSStandort(k.standorte[0]!.kennung);
    } catch (e) {
      melde(e);
    }
  }, [jahr, melde, sStandort]);

  useEffect(() => {
    void holen();
  }, [holen]);

  const tun = (vorgang: Promise<unknown>, danach?: (ergebnis: unknown) => void): void => {
    setzeHinweis(null);
    void vorgang
      .then((ergebnis) => {
        danach?.(ergebnis);
        return holen();
      })
      .catch(melde);
  };

  /*
   * Entfernen aus der Rückfrage (CRM-104): Ein Fehler bleibt im Pop-up und
   * nicht auf der Seite; erst der Erfolg schließt es. Das Nachladen danach
   * meldet seine Fehler wie bisher über `holen`.
   */
  const entferne = async (aufruf: () => Promise<unknown>): Promise<void> => {
    await aufruf();
    setzeNachfrage(null);
    setzeHinweis(null);
    void holen();
  };

  const laender = daten === null ? [] : [...new Set(daten.standorte.map((s) => s.bundesland))];
  const standortname = (kennung: string): string =>
    daten?.standorte.find((s) => s.kennung === kennung)?.bezeichnung ?? kennung;

  /*
   * DAS RASTER ZEIGT EINEN STANDORT: seine Schließtage und die Feiertage, die
   * dort gelten — bundesweite und die seines Bundeslands (FRS-08). Die
   * Tabellen darunter zeigen weiter alle Standorte des Jahres.
   */
  const bundesland = daten?.standorte.find((s) => s.kennung === sStandort)?.bundesland;
  const feiertageAm = (tag: string): readonly Feiertag[] =>
    (daten?.feiertage ?? []).filter(
      (f) => f.tag === tag && (f.geltung === 'DE' || f.geltung === bundesland),
    );
  const schliesstageAm = (tag: string): readonly Schliesstag[] =>
    (daten?.schliesstage ?? []).filter((s) => s.tag === tag && s.standort === sStandort);

  /** Ein Monat weiter oder zurück; am Jahresrand wechselt das Jahr mit. */
  const blaettere = (schritt: number): void => {
    const neu = monat + schritt;
    if (neu < 0 || neu > 11) setzeJahr((j) => j + (neu < 0 ? -1 : 1));
    setzeMonat((neu + 12) % 12);
    setzeGewaehlt(null);
  };

  const beginn = rasterbeginn(jahr, monat);
  const tage = Array.from({ length: 42 }, (_, i) => {
    const d = new Date(beginn);
    d.setUTCDate(beginn.getUTCDate() + i);
    return d;
  });

  const werkzeuge = (
    <>
      <label className="liste__filter">
        Jahr{' '}
        <input
          id="kalenderjahr"
          type="number"
          aria-label="Jahr"
          value={jahr}
          min={2000}
          max={2100}
          onChange={(e) => {
            const j = Number(e.target.value);
            if (Number.isInteger(j) && j >= 2000 && j <= 2100) {
              setzeJahr(j);
              setzeGewaehlt(null);
            }
          }}
        />
      </label>
      <div className="knopfreihe">
        <button type="button" className="knopf knopf--klein" onClick={() => blaettere(-1)}>
          ← Vormonat
        </button>
        <button type="button" className="knopf knopf--klein" onClick={() => blaettere(1)}>
          Folgemonat →
        </button>
      </div>
      {daten !== null && daten.standorte.length > 0 && (
        <label className="liste__filter">
          Standort{' '}
          <select
            aria-label="Standort"
            value={sStandort}
            onChange={(e) => {
              setzeSStandort(e.target.value);
            }}
          >
            {daten.standorte.map((s) => (
              <option key={s.kennung} value={s.kennung}>
                {s.bezeichnung}
              </option>
            ))}
          </select>
        </label>
      )}
      {darfPflegen && (
        <button
          type="button"
          className={daten?.pflegeFaellig === true ? 'knopf knopf--haupt' : 'knopf'}
          onClick={() => {
            const bis = new Date().getFullYear() + 5;
            tun(feiertageEintragen(bis), (e) => {
              setzeHinweis(`${String((e as { eingetragen: number }).eingetragen)} Feiertage bis ${String(bis)} eingetragen.`);
            });
          }}
        >
          Feiertage für die nächsten fünf Jahre eintragen
        </button>
      )}
    </>
  );

  /*
   * DIE SEITENLEISTE: der gewählte Tag und die Pflege. Ein Klick in den Monat
   * wählt den Tag und setzt ihn in beide Formulare — getippt wird er nur
   * noch, wo er außerhalb des Monats liegt. Wer nicht pflegen darf und
   * keinen Tag gewählt hat, bekommt die ganze Breite für den Kalender.
   */
  const seite =
    !darfPflegen && gewaehlt === null ? undefined : (
      <>
        {gewaehlt !== null && (
          <section aria-label="Gewählter Tag">
            <div className="karte__titel">
              <h2>{tagform.format(new Date(`${gewaehlt}T12:00:00Z`))}</h2>
              <small>{standortname(sStandort)}</small>
            </div>
            {feiertageAm(gewaehlt).length === 0 && schliesstageAm(gewaehlt).length === 0 ? (
              <p className="hilfe">Weder Feiertag noch Schließtag.</p>
            ) : (
              <ul className="kanalliste">
                {feiertageAm(gewaehlt).map((f) => (
                  <li key={`f-${f.geltung}`}>
                    <span className="marke">Feiertag</span> {f.bezeichnung}
                  </li>
                ))}
                {schliesstageAm(gewaehlt).map((s) => (
                  <li key={`s-${s.standort}`}>
                    <span className="marke marke--warn">geschlossen</span> {s.bezeichnung}
                  </li>
                ))}
              </ul>
            )}
            <div className="knopfreihe">
              <button
                type="button"
                className="knopf knopf--klein"
                onClick={() => {
                  setzeGewaehlt(null);
                }}
              >
                Auswahl aufheben
              </button>
            </div>
          </section>
        )}

        {darfPflegen && daten !== null && (
          <section aria-label="Schließtag eintragen">
            <div className="karte__titel">
              <h2>Schließtag eintragen</h2>
              {/* FRS-08 */}
              <small>{standortname(sStandort)}</small>
            </div>
            <label className="feld">
              <span className="feld__beschriftung">Tag des Schließtags</span>
              <input type="date" value={sTag} onChange={(e) => { setzeSTag(e.target.value); }} />
            </label>
            <label className="feld">
              <span className="feld__beschriftung">Grund des Schließtags</span>
              <input value={sBezeichnung} maxLength={200} onChange={(e) => { setzeSBezeichnung(e.target.value); }} />
            </label>
            <div className="knopfreihe">
              <button
                type="button"
                className="knopf knopf--haupt"
                disabled={sStandort === '' || sTag === '' || sBezeichnung.trim() === ''}
                onClick={() => {
                  tun(
                    schliesstagAnlegen({ standort: sStandort, tag: sTag, bezeichnung: sBezeichnung.trim() }),
                    (e) => {
                      setzeBetroffen((e as { betroffeneTermine: readonly BetroffenerTermin[] }).betroffeneTermine);
                      setzeSTag('');
                      setzeSBezeichnung('');
                    },
                  );
                }}
              >
                Schließtag eintragen
              </button>
            </div>
            {betroffen.length > 0 && (
              <div role="alert" className="warnung">
                <p>
                  An diesem Tag stehen schon {betroffen.length} Termine. Sie nehmen keine Buchung mehr an — sagen
                  Sie sie ab oder verlegen Sie sie:
                </p>
                <ul>
                  {betroffen.map((t) => (
                    <li key={t.id}>
                      {new Date(t.beginn).toLocaleString('de-DE')} — {t.artikel}, {t.gebuchtePlaetze} Plätze gebucht
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}

        {darfPflegen && (
          <section aria-label="Feiertag ergänzen">
            <div className="karte__titel">
              <h2>Feiertag ergänzen</h2>
            </div>
            <label className="feld">
              <span className="feld__beschriftung">Gilt für</span>
              <select value={fGeltung} onChange={(e) => { setzeFGeltung(e.target.value); }}>
                <option value="">— bitte wählen —</option>
                <option value="DE">bundesweit</option>
                {laender.map((l) => (
                  <option key={l} value={l}>{LAENDER[l] ?? l}</option>
                ))}
              </select>
            </label>
            <label className="feld">
              <span className="feld__beschriftung">Tag des Feiertags</span>
              <input type="date" value={fTag} onChange={(e) => { setzeFTag(e.target.value); }} />
            </label>
            <label className="feld">
              <span className="feld__beschriftung">Bezeichnung des Feiertags</span>
              <input value={fBezeichnung} maxLength={200} onChange={(e) => { setzeFBezeichnung(e.target.value); }} />
            </label>
            <div className="knopfreihe">
              <button
                type="button"
                className="knopf"
                disabled={fGeltung === '' || fTag === '' || fBezeichnung.trim() === ''}
                onClick={() => {
                  tun(feiertagAnlegen({ geltung: fGeltung, tag: fTag, bezeichnung: fBezeichnung.trim() }), () => {
                    setzeFTag('');
                    setzeFBezeichnung('');
                  });
                }}
              >
                Feiertag ergänzen
              </button>
            </div>
          </section>
        )}
      </>
    );

  return (
    /*
     * SEITENTYP PLANUNG (ÄA-41): oben Jahr, Monat und Standort, darunter der
     * Monat in voller Breite und die Tabellen des Jahres, rechts der gewählte
     * Tag und die Pflege.
     */
    <Planung
      werkzeuge={werkzeuge}
      seitenBeschriftung="Gewählter Tag und Pflege"
      {...(seite === undefined ? {} : { seite })}
      oben={
        <>
          <p className="hilfe">
            Gesetzliche Feiertage verschieben die Zahlungsfrist auf den nächsten Werktag. An einem
            Schließtag entsteht am Standort kein Termin, und schon geplante Termine nehmen keine Buchung mehr
            an. Trainings an Feiertagen bleiben möglich — ist der Standort zu, tragen Sie einen Schließtag ein.
          </p>
          {fehler !== null && <Meldung fehler={fehler} />}
          {hinweis !== null && <p role="status" className="hilfe">{hinweis}</p>}
          {daten !== null && (
            <p role={daten.pflegeFaellig ? 'alert' : 'status'} className={daten.pflegeFaellig ? 'warnung' : 'hilfe'}>
              {daten.gepflegtBis === null
                ? 'Es sind keine Feiertage eingetragen.'
                : `Feiertage eingetragen bis ${alsDatum(daten.gepflegtBis)}.`}
              {/* FRS-09 */}
              {daten.pflegeFaellig && ' Das sind weniger als zwölf Monate — bitte die nächsten Jahre eintragen.'}
            </p>
          )}
        </>
      }
    >
      <section className="karte" aria-label="Monat">
        <div className="karte__titel">
          <h2>{monatform.format(new Date(Date.UTC(jahr, monat, 15)))}</h2>
          <small>{sStandort === '' ? '' : standortname(sStandort)}</small>
        </div>
        <div className="tabellenhuelle tabellenhuelle--telefon">
          <table className="liste__tabelle kalender">
            <caption className="nur-vorlesen">
              Monatsübersicht: je Tag, ob am Standort ein Feiertag gilt oder geschlossen ist
            </caption>
            <thead>
              <tr>
                {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((w) => (
                  <th key={w} scope="col">
                    {w}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[0, 1, 2, 3, 4, 5].map((woche) => (
                <tr key={woche}>
                  {tage.slice(woche * 7, woche * 7 + 7).map((d) => {
                    const tag = alsTag(d);
                    const feier = feiertageAm(tag);
                    const zu = schliesstageAm(tag);
                    const klassen = [
                      d.getUTCMonth() !== monat ? 'kalender__fremd' : '',
                      tag === gewaehlt ? 'kalender__gewaehlt' : '',
                    ].filter((k) => k !== '').join(' ');
                    /*
                     * Die Namen stehen im Vorlesetext und in der Seitenleiste,
                     * im Kasten nur die Art: Ein Feiertagsname in jedem Kasten
                     * bräche die Zeilen des Rasters um.
                     */
                    const vorlesen = [
                      tagform.format(d),
                      ...feier.map((f) => `Feiertag ${f.bezeichnung}`),
                      ...zu.map((s) => `geschlossen: ${s.bezeichnung}`),
                    ].join(', ');
                    return (
                      <td key={tag} className={klassen === '' ? undefined : klassen}>
                        <button
                          type="button"
                          className="kalender__knopf"
                          aria-label={vorlesen}
                          aria-pressed={tag === gewaehlt}
                          onClick={() => {
                            setzeGewaehlt(tag);
                            setzeSTag(tag);
                            setzeFTag(tag);
                          }}
                        >
                          <span className="kalender__tag">{d.getUTCDate()}</span>
                          {feier.length > 0 && (
                            <span className="kalender__zahl">
                              <span className="marke">Feiertag</span>
                            </span>
                          )}
                          {zu.length > 0 && (
                            <span className="kalender__zahl">
                              <span className="marke marke--warn">geschlossen</span>
                            </span>
                          )}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="karte" aria-label="Schließtage">
        <div className="karte__titel">
          <h2>Schließtage {jahr}</h2>
          <small>alle Standorte</small>
        </div>
        {daten === null || daten.schliesstage.length === 0 ? (
          <p className="hilfe">Keine Schließtage eingetragen.</p>
        ) : (
          <div className="tabellenhuelle tabellenhuelle--telefon">
            <table className="liste__tabelle" aria-label="Schließtage">
              <thead>
                <tr><th scope="col">Tag</th><th scope="col">Standort</th><th scope="col">Grund</th><th scope="col" /></tr>
              </thead>
              <tbody>
                {daten.schliesstage.map((s) => (
                  <tr key={`${s.standort}-${s.tag}`}>
                    <td className="liste__einzeilig">{alsDatum(s.tag)}</td>
                    <td>{standortname(s.standort)}</td>
                    <td>{s.bezeichnung}</td>
                    <td>
                      {darfPflegen && (
                        <button
                          type="button"
                          className="knopf knopf--eingriff knopf--klein"
                          onClick={() => {
                            setzeNachfrage({ art: 'schliesstag', eintrag: s });
                          }}
                        >
                          Entfernen …
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="karte" aria-label="Gesetzliche Feiertage">
        <div className="karte__titel">
          <h2>Gesetzliche Feiertage {jahr}</h2>
          <small>alle Länder der Standorte</small>
        </div>
        {daten === null || daten.feiertage.length === 0 ? (
          <p className="hilfe">Für dieses Jahr sind keine Feiertage eingetragen.</p>
        ) : (
          <div className="tabellenhuelle tabellenhuelle--telefon">
            <table className="liste__tabelle" aria-label="Gesetzliche Feiertage">
              <thead>
                <tr><th scope="col">Tag</th><th scope="col">Feiertag</th><th scope="col">Gilt</th><th scope="col" /></tr>
              </thead>
              <tbody>
                {daten.feiertage.map((f) => (
                  <tr key={`${f.geltung}-${f.tag}`}>
                    <td className="liste__einzeilig">{alsDatum(f.tag)}</td>
                    <td>{f.bezeichnung}</td>
                    <td>{LAENDER[f.geltung] ?? f.geltung}</td>
                    <td>
                      {darfPflegen && (
                        <button
                          type="button"
                          className="knopf knopf--eingriff knopf--klein"
                          onClick={() => {
                            setzeNachfrage({ art: 'feiertag', eintrag: f });
                          }}
                        >
                          Entfernen …
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* FRS-08: Am Schließtag entsteht kein Termin, ein stehender nimmt keine Buchung an. */}
      {darfPflegen && nachfrage?.art === 'schliesstag' && (
        <Rueckfrage
          frage="Schließtag entfernen?"
          unterzeile={`${standortname(nachfrage.eintrag.standort)} · ${alsDatum(nachfrage.eintrag.tag)}`}
          tat="Entfernen"
          beiSchliessen={() => {
            setzeNachfrage(null);
          }}
          beiBestaetigen={() =>
            entferne(() => schliesstagLoeschen(nachfrage.eintrag.standort, nachfrage.eintrag.tag))
          }
        >
          <p>
            „{nachfrage.eintrag.bezeichnung}" entfällt: {standortname(nachfrage.eintrag.standort)} ist
            am {alsDatum(nachfrage.eintrag.tag)} wieder geöffnet: Termine lassen sich anlegen, und
            stehende nehmen wieder Buchungen an.
          </p>
        </Rueckfrage>
      )}

      {/* FRS-05: Ein Feiertag verschiebt die Zahlungsfrist auf den nächsten Werktag. */}
      {darfPflegen && nachfrage?.art === 'feiertag' && (
        <Rueckfrage
          frage="Feiertag entfernen?"
          unterzeile={`${nachfrage.eintrag.bezeichnung} · ${alsDatum(nachfrage.eintrag.tag)}`}
          tat="Entfernen"
          beiSchliessen={() => {
            setzeNachfrage(null);
          }}
          beiBestaetigen={() =>
            entferne(() => feiertagLoeschen(nachfrage.eintrag.geltung, nachfrage.eintrag.tag))
          }
        >
          <p>
            Der {alsDatum(nachfrage.eintrag.tag)} zählt dann{' '}
            {nachfrage.eintrag.geltung === 'DE'
              ? 'bundesweit'
              : `in ${LAENDER[nachfrage.eintrag.geltung] ?? nachfrage.eintrag.geltung}`}{' '}
            nicht mehr als Feiertag: Eine Zahlungsfrist, die auf ihn fällt, verschiebt sich nicht
            mehr auf den nächsten Werktag.
          </p>
        </Rueckfrage>
      )}
    </Planung>
  );
}
